import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	catchError,
	combineLatest,
	EMPTY,
	map,
	Observable,
	of,
	shareReplay,
	Subscription,
	tap
} from 'rxjs';
import { FundConstants } from '../../../constants/fund.constants';
import {
	agreementRequest,
	Asset,
	createIssuanceRequest,
	Fund,
	FundPageName,
	FundPages,
	FundTransactionStatus,
	IssuanceTransaction,
	fundLoadOptions,
	navHistory,
	navHistoryFilter,
	redemptionRequest,
	RedemptionTransaction,
	cancelRedemptionRequest,
	CatchErrorOption,
	FundPopupType,
	FundPopupModel,
	BankInfo
} from '../models/funds.model';
import {
	BaseService,
	LoadingService,
	LocalStorageService,
	NotificationService,
	NotificationType,
	sKey
} from '../../shared/services';
import { IpoService, NoneEtf } from '../../market-watch';
import { checkDateByRangeDates, DateHelper } from '../../../tools';
import { TimeService } from '@easy-lib/modules/shared/services';
import { FundsService } from './funds.service';
import { BankAccount, PaymentService, PaymentOptions } from '../../payment';
import { WalletConstants } from '@easy-lib/modules/wallet-card';

@Injectable({
	providedIn: 'root'
})
export class FundsFacade extends BaseService {
	sub: Subscription = undefined;
	hasTransaction = false;
	transactionReload = new BehaviorSubject<boolean>(false);
	private fundsSubject = new BehaviorSubject<Fund[]>(
		(JSON.parse(this.localStorage.getItem(sKey.funds)) as Fund[]) || []
	);
	private otherFundsSubject = new BehaviorSubject<Fund[]>([]);
	private userFundsSubject = new BehaviorSubject<Asset[]>([]);
	private assetsValueSubject = new BehaviorSubject<number>(0);
	private overlaySubject = new BehaviorSubject<boolean>(false);
	transactionIssuanceSubject = new BehaviorSubject<IssuanceTransaction[]>([]);
	transactionRedemptionSubject = new BehaviorSubject<RedemptionTransaction[]>([]);
	transactionIssuance$ = this.transactionIssuanceSubject.asObservable();
	transactionRedemption$ = this.transactionRedemptionSubject.asObservable();
	transactionAllSubject = new BehaviorSubject<RedemptionTransaction[]>([]);
	transactionAll$ = this.transactionAllSubject.asObservable();
	assetsValue$ = this.assetsValueSubject.asObservable();
	funds$ = this.fundsSubject.asObservable();
	otherFunds$ = this.otherFundsSubject.asObservable();
	userFunds$ = this.userFundsSubject.asObservable();
	showOverlay$ = this.overlaySubject.asObservable();
	loading$ = this.loadingService.loading$;
	retryLoading$ = this.loadingService.retryLoading$;

	private fundPageSubject = new BehaviorSubject<Partial<FundPages>>({
		name: FundPageName.Fund
	});
	fundPageState$ = this.fundPageSubject.pipe(shareReplay());
	perviousFundPage: Partial<FundPages>[] = [{ name: FundPageName.Fund }];
	fundPopup$ = new BehaviorSubject<FundPopupModel>({ popupType: FundPopupType.None });

	serverDate: Date;

	constructor(
		private fundservice: FundsService,
		private notificationService: NotificationService,
		private loadingService: LoadingService,
		public ipoService: IpoService,
		private localStorage: LocalStorageService,
		private readonly timeService: TimeService,
		private moneyPaymentService: PaymentService
	) {
		super();

		this.timeService.serverDateObs$.subscribe((date: Date) => (this.serverDate = date));
	}
	get nonEtfSubscriptions(): NoneEtf[] {
		const noneEtfs = this.ipoService.getNoneEtfs();
		noneEtfs?.forEach(dto => {
			dto.dateStatus = checkDateByRangeDates(
				this.serverDate,
				dto.pureDate,
				dto.pureDateEnd,
				dto.startTime,
				dto.endTime
			);
		});
		return noneEtfs;
	}

	loadFundData() {
		const observable$ = combineLatest([this.loadAllFund(), this.loadUserFunds()]).pipe(
			map(([funds, assets]) => {
				const newAssets = assets.map((asset: Asset) => {
					const fund = funds.find((e: Fund) => e.code === asset.fundCode) as Fund;
					return {
						...asset,
						name: fund?.name,
						lastSync: fund?.lastSync,
						title: fund?.title,
						progressPercents: fund?.progressPercents
					};
				});
				const newFunds = funds.map((fund: Fund) => ({
					...fund,
					details: fund.description.split(',')
				}));
				return {
					allFund: newFunds,
					userFund: newAssets
				};
			}),
			tap(res => {
				if (!res.allFund.length && !res.userFund.length) this.loadingService.retryLoadingOn();
			}),
			tap(res => {
				this.fundsSubject.next(res.allFund);
				this.hasTransaction = res.allFund.some(x => x.hasTransaction);
				res.allFund = res.allFund.filter(t => !res.userFund.map(x => x.fundCode).includes(t.code));
				this.otherFundsSubject.next(res.allFund);

				const fundPage = this.fundPageSubject.value;
				if (fundPage.activeFund && fundPage.name !== FundPageName.NoneEtf) {
					const activeFund = this.fundsSubject.value.find(t => t.code === fundPage.activeFund.code);
					this.fundPageSubject.next({ ...fundPage, activeFund });
				}
			}),
			tap(res => {
				const totalAmount = res.userFund.map(item => item.amount).reduce((prev, curr) => prev + curr, 0);
				this.assetsValueSubject.next(totalAmount);
				this.userFundsSubject.next(res.userFund);
			}),
			shareReplay(1)
		);
		return this.loadingService.showLoaderUntilCompelete(observable$);
	}

	changeFundPage(name: FundPageName, activeFund: Partial<Fund>) {
		this.perviousFundPage.push(this.fundPageSubject.value);
		this.fundPageSubject.next({ name, activeFund });
	}
	backFundPage() {
		this.fundPageSubject.next(this.perviousFundPage.pop());
	}
	_banksInfo: BankInfo[] = [];
	banksInfo() {
		if (this._banksInfo?.length) return of(this._banksInfo);
		return this.moneyPaymentService.getPaymentOptions().pipe(
			map((res: PaymentOptions) => res?.bankAccounts.filter(t => t.shebaNumber != '')),
			map((res: BankAccount[]) => res?.filter(t => t.id != WalletConstants.BankAccountId)),
			map((res: BankAccount[]) => {
				return res?.map(
					t => ({ bankTitle: t.bankTitle, iban: t.shebaNumber, accountNumber: t.bankAccountCode }) as BankInfo
				);
			}),
			tap(res => {
				this._banksInfo = res;
			})
		);
	}
	loadAllFund(option: CatchErrorOption = { hasErrorNotify: true }) {
		return this.fundservice.funds().pipe(
			map((res: Fund[]) =>
				res.map(obj => ({
					...obj,
					['logo']: FundConstants.FundIcon[obj.code]
				}))
			),
			tap(res => {
				this.localStorage.setItem(sKey.funds, JSON.stringify(res));
			}),
			catchError(error => {
				if (option.hasErrorNotify) {
					this.notificationService.alert(
						error.error?.detail ?? 'نمایش صندوق های سرمایه گذاری با خطا مواجه شد',
						NotificationType.danger
					);
				}
				return of([]);
			})
		);
	}
	loadUserFunds() {
		return this.fundservice.assets().pipe(
			map((res: Asset[]) =>
				res.map(obj => ({
					...obj,
					['logo']: FundConstants.FundIcon[obj.fundCode],
					['date']: DateHelper.getFormattedJDate(new Date(obj.lastSync))
				}))
			),
			catchError(error => {
				this.notificationService.alert(
					error.error?.detail ?? 'نمایش صندوق های من با خطا مواجه شد',
					NotificationType.danger
				);
				return of([]);
			})
		);
	}
	reloadTransactions() {
		this.transactionReload.next(true);
	}
	loadIssuanceTransactions(fundCode: string, loadOption: fundLoadOptions): Observable<IssuanceTransaction[] | any> {
		const observable$ = this.fundservice.issuanceTransactions(fundCode, loadOption).pipe(
			map((res: IssuanceTransaction[]) => res.map(obj => this.issuanceTransactionsInfo(obj))),
			tap((res: IssuanceTransaction[]) => {
				this.transactionIssuanceSubject.next([...this.transactionIssuanceSubject.value, ...res]);
			}),
			// catchError(error => { this.loadingService.retryLoadingOn(); return this.customCatchError(error) }));
			catchError(error => {
				this.notificationService.alert(
					error.error?.detail ?? 'نمایش تراکنش های خرید با خطا مواجه شد',
					NotificationType.danger
				);
				return of([]);
			})
		);
		return this.loadingService.showLoaderUntilCompelete(observable$);
	}

	issuanceTransactionsInfo(obj) {
		return {
			...obj,
			logo: FundConstants.FundIcon[obj.fundCode],
			fundName:
				this.fundsSubject.value.find(t => t.code === obj.fundCode)?.name ??
				FundConstants.FundName[obj.fundCode],
			request: {
				...obj.request,
				stateClass: FundConstants.TransactionStateClass[obj.request.status]
			},
			issueFinancialResult: {
				...obj.issueFinancialResult,
				stateClass: obj.issueFinancialResult?.status
					? FundConstants.TransactionStateClass[obj.issueFinancialResult.status]
					: ''
			},
			fundResult: {
				...obj.fundResult,
				stateClass: FundConstants.TransactionStateClass[obj.fundResult.status]
			},
			issueRemain: {
				...obj.issueRemain,
				stateClass: obj.issueFinancialResult?.status
					? FundConstants.TransactionStateClass[
							obj.fundResult.status === FundTransactionStatus.InProgress
								? FundTransactionStatus.NotStarted
								: obj.fundResult.status
						]
					: ''
			}
		};
	}

	loadAllTransactions(
		fundCode: string,
		loadOption: fundLoadOptions,
		nationalCode?: string | number
	): Observable<any[] | any> {
		const observable$ = this.fundservice.allTransactions(fundCode, loadOption, nationalCode).pipe(
			map((res: any) =>
				res?.item.map(obj =>
					obj.issuanceItem
						? this.issuanceTransactionsInfo(obj.issuanceItem)
						: this.redemptionTransactionsInfo(obj.redemptionItem)
				)
			),
			tap((res: RedemptionTransaction[]) => {
				this.transactionAllSubject.next([...this.transactionAllSubject.value, ...res]);
			}),
			// catchError(error => { this.loadingService.retryLoadingOn(); return this.customCatchError(error) }));
			catchError(error => {
				this.notificationService.alert(
					error.error?.detail ?? 'نمایش همه تراکنش های  با خطا مواجه شد',
					NotificationType.danger
				);
				return of([]);
			})
		);
		return this.loadingService.showLoaderUntilCompelete(observable$);
	}
	loadRedemptionTransactions(
		fundCode: string,
		loadOption: fundLoadOptions
	): Observable<RedemptionTransaction[] | any> {
		const observable$ = this.fundservice.redemptionTransactions(fundCode, loadOption).pipe(
			map((res: RedemptionTransaction[]) => res.map(obj => this.redemptionTransactionsInfo(obj))),
			tap((res: RedemptionTransaction[]) => {
				this.transactionRedemptionSubject.next([...this.transactionRedemptionSubject.value, ...res]);
			}),
			// catchError(error => { this.loadingService.retryLoadingOn(); return this.handleError(error) }));
			catchError(error => {
				this.notificationService.alert(
					error.error?.detail ?? 'نمایش تراکنش های فروش با خطا مواجه شد',
					NotificationType.danger
				);
				return of([]);
			})
		);
		return this.loadingService.showLoaderUntilCompelete(observable$);
	}

	redemptionTransactionsInfo(obj) {
		return {
			...obj,
			logo: FundConstants.FundIcon[obj.fundCode],
			fundName:
				this.fundsSubject.value.find(t => t.code === obj.fundCode)?.name ??
				FundConstants.FundName[obj.fundCode],
			request: {
				...obj.request,
				stateClass: FundConstants.TransactionStateClass[obj.request.status]
			},
			result: {
				...obj.result,
				stateClass: obj.result?.status ? FundConstants.TransactionStateClass[obj.result.status] : ''
			}
		};
	}

	indexAverage(days: number): Observable<number> {
		return this.fundservice.indexAverage(days).pipe(shareReplay());
	}
	navHistory(filter: navHistoryFilter) {
		return this.fundservice.navHistory(filter).pipe(
			map(res =>
				res.sort(this.navDateSorter).map(data => ({
					d: DateHelper.getFormattedJDate(new Date(data.navDate)),
					v: data.issuanceNav,
					v2: data.redemptionNav
				}))
			),
			catchError(error => this.customCatchError(error))
		);
	}
	navDateSorter = (d1: navHistory, d2: navHistory) => (d2.navDate < d1.navDate ? 1 : -1);
	showFundDetail(fundCode: string) {
		const fund = this.fundsSubject.value.find(x => x.code === fundCode && !x.isTemporary) ?? {
			name: '',
			code: '',
			logo: ''
		};
		this.changeFundPage(FundPageName.FundDetail, fund);
	}

	showEtf(fundCode: string) {
		const funds = (JSON.parse(this.localStorage.getItem(sKey.funds)) as Fund[]) || [];
		if (funds.length) {
			const fund = funds.find(x => x.code === fundCode && !x.isTemporary) ?? {
				name: '',
				code: '',
				logo: ''
			};
			this.changeFundPage(FundPageName.FundDetail, fund);
		}
	}

	showNoneEtf(fundCode: string) {
		// const fund = this.fundsSubject.value.find(x => x.code === fundCode && !x.isTemporary) ?? { name: '', code: '', logo: '' };
		this.changeFundPage(FundPageName.NoneEtf, { code: fundCode });
	}
	showTransactions() {
		this.changeFundPage(FundPageName.Transaction, this.fundPageSubject.value?.activeFund);
	}
	resetFundPages() {
		this.perviousFundPage = [];
		this.fundPageSubject.next({ name: FundPageName.Fund });
	}
	createIssuance(req: createIssuanceRequest) {
		const observable$ = this.fundservice.issuance(req).pipe(
			tap(() => {
				this.notificationService.alert('درخواست صدور با موفقیت ثبت شد', NotificationType.success);
			}),
			catchError(error => {
				this.handleErrorNotify(error);
				return EMPTY;
			})
		);
		return this.loadingService.showLoaderUntilCompelete(observable$);
	}
	cancelIssuance(requestId: string) {
		const observable$ = this.fundservice.cancel(requestId).pipe(
			tap(() => {
				this.notificationService.alert('لغو درخواست صدور با موفقیت انجام شد', NotificationType.success);
			}),
			catchError(error => {
				this.handleErrorNotify(error);
				return EMPTY;
			})
		);
		return this.loadingService.showLoaderUntilCompelete(observable$);
	}

	roundingUpward(fund, isNoneEtfSubscription) {
		const fundTotalIssuancePrice = this.calculateFundTotalIssuancePrice(fund, isNoneEtfSubscription).toString();
		const decimalSeparator = fundTotalIssuancePrice.toString().slice(0, fundTotalIssuancePrice.length - 3);
		const decimalWithoutSeparator = fundTotalIssuancePrice.slice(-3);
		const separatorDecimalsFromNonDecimals = decimalSeparator + '.' + decimalWithoutSeparator;
		const convertToDecimal = Math.ceil(+separatorDecimalsFromNonDecimals);
		return +(convertToDecimal + '000');
	}

	redemptionIssuance(req: redemptionRequest) {
		const observable$ = this.fundservice.redeem(req).pipe(
			tap(() => {
				this.notificationService.alert('درخواست ابطال با موفقیت ثبت شد', NotificationType.success);
			}),
			catchError(error => {
				this.handleErrorNotify(error);
				return EMPTY;
			})
		);
		return this.loadingService.showLoaderUntilCompelete(observable$);
	}

	cancelRedemptionIssuance(request: cancelRedemptionRequest) {
		const observable$ = this.fundservice.cancelRedemption(request).pipe(
			tap(() => {
				this.notificationService.alert('لغو درخواست ابطال با موفقیت انجام شد', NotificationType.success);
			}),
			catchError(error => {
				this.handleErrorNotify(error);
				return EMPTY;
			})
		);
		return this.loadingService.showLoaderUntilCompelete(observable$);
	}

	agreement(req: agreementRequest) {
		const observable$ = this.fundservice.agreement(req).pipe(catchError(() => EMPTY));
		return this.loadingService.showLoaderUntilCompelete(observable$);
	}
	consultationReauest() {
		const observable$ = this.fundservice.consultationRequest();
		return observable$ //this.loadingService.showLoaderUntilCompelete(observable$)
			.pipe(
				tap(() => this.notificationService.alert('درخواست شما با موفقیت ثبت شد', NotificationType.success)),
				catchError(error => {
					this.handleErrorNotify(error);
					return EMPTY;
				})
			);
	}
	showOverlay() {
		this.overlaySubject.next(true);
	}
	hideOverlay() {
		this.overlaySubject.next(false);
		this.updateFundPopup(FundPopupType.None);
	}
	calculateFundTotalIssuancePrice(fund: Fund, isNoneEtfSubscription): number {
		if (isNoneEtfSubscription) return fund.issuanceNav;
		const price = Math.ceil(fund.issuanceNav * +(fund?.nextNavEstimate ?? 1.1) + +(fund?.issuancestaticwage ?? 0));
		return price > 1_000_000 ? price : 1_000_000;
	}
	calculateCountOfAssetToBuyByPrice(fund: Fund, price: number, isNoneEtfSubscription: boolean): number {
		price -= +(fund?.issuancestaticwage ?? 0);
		if (isNoneEtfSubscription) return Math.floor(price / fund.issuanceNav);
		const hasNextNavEstimate = +(fund?.nextNavEstimate ?? 1.1);
		return Math.floor(price / (fund.issuanceNav * hasNextNavEstimate));
	}
	private customCatchError(error: any): Observable<any> {
		this.handleErrorNotify(error);
		return this.httpService.handleError(error);
	}
	handleErrorNotify(error: any, defaultError = 'درخواست با خطا مواجه شد') {
		let message = defaultError;
		if (error?.error?.detail) message = error.error.detail;
		else if (typeof error.error == 'string') message = error.error;
		this.notificationService.alert(message, NotificationType.danger);
	}
	updateFundPopup(popupType: FundPopupType, value: string | number | unknown = '') {
		this.fundPopup$.next({ popupType: popupType, value: value });
	}
	defaultFunds = () => {
		const defaultFunds: Fund[] = [];
		for (const [key, value] of Object.entries(FundConstants.FundName)) {
			defaultFunds.push({
				description: '',
				details: [],
				agreementData: [],
				name: value,
				title: '',
				code: key,
				redemptionNav: 0,
				issuanceNav: 0,
				lastSync: null,
				totalAsset: 0,
				totalInvestor: 0,
				issuancestaticwage: 0,
				redemtionstaticwage: 0,
				penalties: [],
				progressPercents: [
					{
						name: 'هفتگی',
						value: 0,
						days: 7
					},
					{
						name: 'ماهانه',
						value: 0,
						days: 31
					},
					{
						name: '3 ماه',
						value: 0,
						days: 90
					},
					{
						name: 'یک سال',
						value: 0,
						days: 365
					}
				],
				profitInfo: null,
				isTemporary: true
			});
		}
		return defaultFunds;
	};
	showNotify(text: string, notifType: NotificationType) {
		this.notificationService.alert(text, notifType);
	}
}
